// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
import SvgColor from '../../../components/svg-color';

// ----------------------------------------------------------------------

// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

const icon = (name: string) => <Iconify icon={name} sx={{ width: 1, height: 1 }} />;

const ICONS = {
  blog: icon('solar:notes-bold-duotone'),
  cart: icon('solar:cart-large-2-bold-duotone'),
  chat: icon('solar:chat-round-dots-bold-duotone'),
  mail: icon('solar:letter-bold-duotone'),
  user: icon('solar:user-rounded-bold-duotone'),
  file: icon('solar:file-text-bold-duotone'),
  lock: icon('solar:lock-password-bold-duotone'),
  label: icon('solar:tag-horizontal-bold-duotone'),
  blank: icon('solar:document-bold-duotone'),
  kanban: icon('solar:checklist-minimalistic-bold-duotone'),
  folder: icon('solar:folder-2-bold-duotone'),
  banking: icon('solar:wad-of-money-bold-duotone'),
  booking: icon('solar:calendar-date-bold-duotone'),
  invoice: icon('solar:bill-list-bold-duotone'),
  calendar: icon('solar:calendar-bold-duotone'),
  disabled: icon('solar:forbidden-bold-duotone'),
  external: icon('solar:square-share-line-bold-duotone'),
  menuItem: icon('solar:menu-dots-square-bold-duotone'),
  ecommerce: icon('solar:bag-2-bold-duotone'),
  analytics: icon('solar:chart-square-bold-duotone'),
  dashboard: icon('solar:widget-5-bold-duotone'),
  upsell: icon('solar:star-fall-bold-duotone'),
};

const NavConfig = (t: any, currentUser: any = {}) => {
  const userRole = String(currentUser?.role || '').toUpperCase();
  const isManager = userRole === 'MANAGER' || userRole === '12';
  const isAdminEmail = currentUser?.email === 'admin@ailyn.io';

  // Global Admin: Sees everything
  const isGlobalAdmin = userRole.includes('SUPER_ADMIN') || userRole === '0' || isAdminEmail;
  // Org Admin: Sees only their organization's sub-orgs, users, billing
  const isOrgAdmin = userRole === 'ADMIN';
  const isSupport = userRole === 'SUPPORT';

  const isAnyAdmin = isGlobalAdmin || isOrgAdmin;
  const hasRole = userRole !== '';

  const parentOrgId = currentUser?.parentOrgId || '';
  const subOrgId = currentUser?.subOrgId || '';

  const generalItems = [
    { title: 'nav.operational', path: PATH_DASHBOARD.general.app, icon: ICONS.dashboard },
    ...(!isManager ? [
      { title: 'nav.analytics', path: PATH_DASHBOARD.general.analytics, icon: ICONS.analytics },
    ] : []),
    ...(hasRole && !isAnyAdmin && !isManager && !isSupport ? [
      { 
        title: 'nav.products', 
        path: PATH_DASHBOARD.eCommerce.list,
        icon: ICONS.ecommerce 
      },
      { 
        title: 'nav.commands', 
        path: PATH_DASHBOARD.invoice.list,
        icon: ICONS.invoice 
      },
      {
        title: 'nav.categories',
        path: '/dashboard/category/list',
        icon: ICONS.menuItem,
      },
      {
        title: 'nav.billing',
        path: PATH_DASHBOARD.invoice.list,
        icon: ICONS.banking,
      }
    ] : [])
  ];

  const managementItems = [];

  if (hasRole && !isManager) {
    if (isGlobalAdmin) {
      managementItems.push({
        title: 'nav.all_organizations',
        path: PATH_DASHBOARD.user.list,
        icon: ICONS.external,
        key: 'all-organizations-link',
      });
    }

    if (isAnyAdmin) {
      managementItems.push({
        title: 'nav.sub_organizations_list',
        path: '/dashboard/sub-organization/list',
        icon: ICONS.blog,
        key: 'global-sub-org-list',
      });
      if (isOrgAdmin) {
        managementItems.push({
          title: 'nav.commands',
          path: '/dashboard/orders/all',
          icon: ICONS.ecommerce,
          key: 'org-admin-orders',
        });
      }
      managementItems.push({
        title: 'nav.users',
        path: PATH_DASHBOARD.user.allUsers,
        icon: ICONS.user,
        key: 'global-users-all',
      });
      managementItems.push({
        title: 'nav.billing',
        path: PATH_DASHBOARD.invoice.list,
        icon: ICONS.invoice,
        key: 'global-billing-list',
      });
      
      if (isGlobalAdmin) {
        managementItems.push({
          title: 'nav.company_settings',
          path: PATH_DASHBOARD.admin.companySettings,
          icon: ICONS.file,
          key: 'global-company-settings',
        });
        managementItems.push({
          title: 'nav.action_logs',
          path: PATH_DASHBOARD.admin.actionLogs,
          icon: ICONS.file,
          key: 'global-action-logs',
        });
      }
    }
  }

  if (isSupport) {
    managementItems.push({
      title: 'nav.sub_organizations_list',
      path: '/dashboard/sub-organization/list',
      icon: ICONS.blog,
      key: 'support-sub-org-list',
    });
    managementItems.push({
      title: 'nav.commands',
      path: '/dashboard/orders/all',
      icon: ICONS.ecommerce,
      key: 'support-orders',
    });
    managementItems.push({
      title: 'nav.products',
      path: parentOrgId && subOrgId
        ? `/dashboard/products/${parentOrgId}/${subOrgId}/list`
        : '/dashboard/sub-organization/list',
      icon: ICONS.cart,
      key: 'support-products',
    });
    managementItems.push({
      title: 'nav.categories',
      path: '/dashboard/category/list',
      icon: ICONS.menuItem,
      key: 'support-categories',
    });
    managementItems.push({
      title: 'nav.billing',
      path: PATH_DASHBOARD.invoice.list,
      icon: ICONS.invoice,
      key: 'support-billing',
    });
    managementItems.push({
      title: 'nav.Pos_config',
      path: parentOrgId && subOrgId
        ? `/dashboard/organization/${parentOrgId}/branches/${subOrgId}/settings`
        : '/dashboard/sub-organization/list',
      icon: ICONS.analytics,
      key: 'support-pos-status',
    });
    managementItems.push({
      title: 'nav.action_logs',
      path: PATH_DASHBOARD.admin.actionLogs,
      icon: ICONS.file,
      key: 'support-action-logs',
    });
  }

  if (isManager) {
    managementItems.push({
      title: 'nav.products',
      path: `/dashboard/products/${parentOrgId}/${subOrgId}/list`,
      icon: ICONS.ecommerce,
      key: 'manager-products',
    });
    managementItems.push({
      title: 'nav.commands',
      path: `/dashboard/orders/list/${subOrgId}`,
      icon: ICONS.invoice,
      key: 'manager-commands',
    });
    managementItems.push({
      title: 'nav.categories',
      path: `/dashboard/category/list/${subOrgId}`,
      icon: ICONS.menuItem,
      key: 'manager-categories',
    });
    managementItems.push({
      title: 'nav.billing',
      path: `/dashboard/invoice/list/${subOrgId}`,
      icon: ICONS.banking,
      key: 'manager-billing',
    });
    managementItems.push({
      title: 'nav.parent_organization',
      path: PATH_DASHBOARD.user.branch.parentInfo(parentOrgId, subOrgId),
      icon: ICONS.external,
      key: 'parent-org-info',
    });
    
    managementItems.push({
      title: 'nav.staff',
      path: `/dashboard/sub-organization-users/${parentOrgId}/${subOrgId}/list`,
      icon: ICONS.user,
      key: 'staff-management',
    });

    managementItems.push({
      title: 'nav.settings',
      path: `/dashboard/organization/${parentOrgId}/branches/${subOrgId}/settings`,
      icon: ICONS.analytics,
      key: 'branch-settings',
    });

    // --- UPSELLING ---
    const createdAt = currentUser?.createdAt ? new Date(currentUser.createdAt).getTime() : 0;
    const now = new Date().getTime();
    const isMoreThan48h = createdAt > 0 && now > createdAt + 48 * 60 * 60 * 1000;
    
    managementItems.push({
      title: 'nav.upselling',
      path: `/dashboard/upselling`,
      icon: ICONS.upsell,
      info: isMoreThan48h ? <span style={{ 
        backgroundColor: '#00B8D9', 
        color: '#fff', 
        padding: '2px 6px', 
        borderRadius: '4px', 
        fontSize: '10px', 
        fontWeight: 'bold' 
      }}>{t('nav.new_badge')}</span> : null,
      key: 'upselling-module',
    });

  }


  return [
    // GENERAL
    {
      subheader: 'nav.general',
      items: generalItems,
    },
    // MANAGEMENT
    {
      subheader: 'nav.management',
      items: managementItems,
    },
  ];
};

export default NavConfig;